import React, { useState, useMemo } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC } from '../../shared/styles';

/* ─── Program definitions with weekly exercise schedules ──────────── */

const PROGRAMS = [
  {
    id: 'mind-mastery',
    icon: '🧠',
    color: '#2ABFCF',
    title: '12-Week Mind Mastery',
    titleNL: '12-Weken Geestbeheersing',
    subtitle: 'Cognitive & mindfulness training',
    subtitleNL: 'Cognitieve & mindfulness training',
    weeks: 12,
    sessionsPerWeek: 3,
    // Each week: array of day-sessions [{day, exIds}]
    // day = 1 Mon, 2 Tue... (shown as labels only)
    schedule: [
      // Weeks 1-3: Foundation
      { exIds: ['breathing', 'gratitude'] },
      { exIds: ['meditation'] },
      { exIds: ['bodyscan'] },
      // Weeks 4-6: Deepen
      { exIds: ['boxbreathing', 'focus'] },
      { exIds: ['meditation', 'grounding_541'] },
      { exIds: ['bodyscan', 'emotion_reg'] },
      // Weeks 7-9: Integrate
      { exIds: ['coherent', 'focus'] },
      { exIds: ['breathing478', 'gratitude'] },
      { exIds: ['emotional_clarity', 'bodyscan'] },
      // Weeks 10-12: Mastery
      { exIds: ['wimhof', 'focus'] },
      { exIds: ['meditation', 'perspective_mirror'] },
      { exIds: ['coherent', 'emotional_clarity'] },
    ],
  },
  {
    id: 'athlete-protocol',
    icon: '🏋️',
    color: '#E8922A',
    title: "The Athlete's Protocol",
    titleNL: 'Het Atleten Protocol',
    subtitle: 'High-performance physical & mental conditioning',
    subtitleNL: 'Hoge prestaties — lichaam & geest',
    weeks: 8,
    sessionsPerWeek: 3,
    schedule: [
      // Weeks 1-2: Activation
      { exIds: ['powerpose', 'focus'] },
      { exIds: ['stretching', 'breathing'] },
      { exIds: ['walking', 'gratitude'] },
      // Weeks 3-5: Build
      { exIds: ['wimhof', 'powerpose'] },
      { exIds: ['yoga', 'focus'] },
      { exIds: ['stretching', 'bodyscan'] },
      // Weeks 6-8: Peak
      { exIds: ['wimhof', 'focus'] },
      { exIds: ['yoga', 'breathing478'] },
      { exIds: ['coherent', 'meditation'] },
    ],
  },
  {
    id: 'deep-recovery',
    icon: '🌿',
    color: '#4AC87A',
    title: 'Deep Recovery Reset',
    titleNL: 'Diepe Herstelmodus',
    subtitle: 'Burnout recovery — gentle, evidence-based',
    subtitleNL: 'Burnoutherstel — zacht en bewezen effectief',
    weeks: 4,
    sessionsPerWeek: 3,
    schedule: [
      // Week 1: Ground
      { exIds: ['bodyscan'] },
      { exIds: ['breathing', 'gratitude'] },
      { exIds: ['grounding_541'] },
      // Week 2: Soften
      { exIds: ['meditation', 'bodyscan'] },
      { exIds: ['breathing478', 'gratitude'] },
      { exIds: ['emotion_reg'] },
      // Week 3: Restore
      { exIds: ['coherent', 'bodyscan'] },
      { exIds: ['meditation', 'emotional_clarity'] },
      { exIds: ['grounding_541', 'gratitude'] },
      // Week 4: Integrate
      { exIds: ['meditation', 'coherent'] },
      { exIds: ['emotional_clarity', 'breathing478'] },
      { exIds: ['bodyscan', 'gratitude'] },
    ],
  },
];

const EX_META = {
  breathing:           { icon: '💨', label: 'Breathing',           labelNL: 'Ademhaling' },
  meditation:          { icon: '🧘', label: 'Meditation',          labelNL: 'Meditatie' },
  bodyscan:            { icon: '🌿', label: 'Body Scan',           labelNL: 'Bodyscan' },
  focus:               { icon: '⏱️', label: 'Focus Timer',         labelNL: 'Focus Timer' },
  walking:             { icon: '🚶', label: 'Walking',             labelNL: 'Wandeling' },
  stretching:          { icon: '🤸', label: 'Stretching',          labelNL: 'Stretching' },
  gratitude:           { icon: '🙏', label: 'Gratitude',           labelNL: 'Dankbaarheid' },
  yoga:                { icon: '🧘‍♀️', label: 'Yoga Flow',          labelNL: 'Yoga Flow' },
  boxbreathing:        { icon: '⬛', label: 'Box Breathing',       labelNL: 'Vierkante Adem' },
  breathing478:        { icon: '🌊', label: '4-7-8 Breathing',     labelNL: '4-7-8 Ademhaling' },
  wimhof:              { icon: '🔥', label: 'Power Breathing',     labelNL: 'Kracht Ademhaling' },
  coherent:            { icon: '〰️', label: 'Coherent Breathing',  labelNL: 'Coherente Adem' },
  powerpose:           { icon: '💪', label: 'Power Pose',          labelNL: 'Powerpose' },
  conscious_listening: { icon: '👂', label: 'Conscious Listening', labelNL: 'Bewust Luisteren' },
  perspective_mirror:  { icon: '🪞', label: 'Perspective Mirror',  labelNL: 'Perspectiefspiegel' },
  emotional_clarity:   { icon: '💎', label: 'Emotional Clarity',   labelNL: 'Emotionele Helderheid' },
  grounding_541:       { icon: '🌱', label: '5-4-3-2-1 Grounding', labelNL: '5-4-3-2-1 Grounding' },
  emotion_reg:         { icon: '💗', label: 'Emotion Regulation',  labelNL: 'Emotieregulatietraining' },
};

const PROGRESS_KEY = (id) => `sl_prog_${id}`;

function loadProgress(id) {
  try { return JSON.parse(localStorage.getItem(PROGRESS_KEY(id)) || 'null'); } catch { return null; }
}
function saveProgress(id, data) {
  try { localStorage.setItem(PROGRESS_KEY(id), JSON.stringify(data)); } catch {}
}

/* ─── Session chip ───────────────────────────────────────────────── */
function SessionChip({ exId, nl, done, onStart }) {
  const meta = EX_META[exId] || { icon: '⚡', label: exId, labelNL: exId };
  return (
    <button
      onClick={() => onStart(exId)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
        background: done ? `${_T.accent}22` : 'rgba(255,255,255,0.05)',
        border: `1px solid ${done ? _T.accent : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 20, cursor: 'pointer', fontSize: 12,
        color: done ? _T.accent : C.silver2,
        textDecoration: done ? 'line-through' : 'none',
        transition: 'all 0.15s',
      }}
    >
      <span>{meta.icon}</span>
      <span>{nl ? meta.labelNL : meta.label}</span>
      {done && <span style={{ fontSize: 10 }}>✓</span>}
    </button>
  );
}

/* ─── Program card ───────────────────────────────────────────────── */
function ProgramCard({ program, nl, isPremium, onStartExercise, exHistory }) {
  const [expanded, setExpanded] = useState(false);
  const progress = loadProgress(program.id);
  const isStarted = !!progress;
  const startedDate = isStarted ? new Date(progress.startDate) : null;

  // Calculate current week (1-based)
  const currentWeek = useMemo(() => {
    if (!startedDate) return 1;
    const msPerWeek = 7 * 24 * 3600 * 1000;
    const elapsed = Math.floor((Date.now() - startedDate.getTime()) / msPerWeek);
    return Math.min(program.weeks, elapsed + 1);
  }, [startedDate, program.weeks]);

  const completedWeeks = isStarted ? Math.max(0, currentWeek - 1) : 0;
  const progressPct = Math.round((completedWeeks / program.weeks) * 100);

  // Sessions for this week — 3 per week, index into schedule cyclically
  const weekSessions = useMemo(() => {
    const base = (currentWeek - 1) * program.sessionsPerWeek;
    return [0, 1, 2].map(i => program.schedule[(base + i) % program.schedule.length]);
  }, [currentWeek, program]);

  // Check which sessions are done today (based on exHistory in last 24h)
  const todayMs = Date.now() - 24 * 3600 * 1000;
  const recentExIds = new Set((exHistory || []).filter(e => (e.date || 0) > todayMs).map(e => e.exId));
  const doneKey = (s) => {
    const key = `${program.id}-w${currentWeek}-${s.exIds.join('_')}`;
    return progress?.done?.[key];
  };

  const markDone = (session) => {
    const key = `${program.id}-w${currentWeek}-${session.exIds.join('_')}`;
    const next = { ...progress, done: { ...(progress?.done || {}), [key]: true } };
    saveProgress(program.id, next);
    window.dispatchEvent(new Event('storage')); // trigger re-render
  };

  const handleStart = () => {
    if (!isStarted) {
      saveProgress(program.id, { startDate: Date.now(), done: {} });
    }
  };

  return (
    <div style={{ ...s_GC({ padding: '18px 20px', marginBottom: 14 }) }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 16, flexShrink: 0,
          background: `${program.color}18`, border: `1px solid ${program.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
        }}>
          {program.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: 'Georgia,serif', marginBottom: 2 }}>
            {nl ? program.titleNL : program.title}
          </div>
          <div style={{ color: C.silver2, fontSize: 12, marginBottom: 4 }}>
            {nl ? program.subtitleNL : program.subtitle}
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1,
            background: `${program.color}20`, color: program.color,
            border: `1px solid ${program.color}40`, borderRadius: 20, padding: '2px 9px',
          }}>
            {program.weeks} {nl ? 'WEKEN' : 'WEEKS'} · {program.sessionsPerWeek}×/wk
          </span>
        </div>
        {isStarted && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: C.silver2 }}>{nl ? 'Week' : 'Week'}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: program.color }}>{currentWeek}</div>
            <div style={{ fontSize: 9, color: C.silver2 }}>/{program.weeks}</div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {isStarted && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: C.silver2 }}>{nl ? 'Voortgang' : 'Progress'}</span>
            <span style={{ fontSize: 10, color: program.color, fontWeight: 700 }}>{progressPct}%</span>
          </div>
          <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progressPct}%`, background: `linear-gradient(90deg,${program.color},${program.color}aa)`, borderRadius: 3, transition: 'width 0.5s' }} />
          </div>
        </div>
      )}

      {/* This week's sessions */}
      {isStarted && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.silver2, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>
            {nl ? `WEEK ${currentWeek} — SESSIES` : `WEEK ${currentWeek} — SESSIONS`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {weekSessions.map((session, si) => {
              const done = doneKey(session);
              return (
                <div key={si} style={{
                  background: done ? `${program.color}0a` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${done ? program.color + '33' : 'rgba(255,255,255,0.07)'}`,
                  borderRadius: 12, padding: '10px 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                    {session.exIds.map(exId => (
                      <SessionChip key={exId} exId={exId} nl={nl} done={done || recentExIds.has(exId)} onStart={onStartExercise} />
                    ))}
                  </div>
                  {!done && (
                    <button
                      onClick={() => markDone(session)}
                      style={{ fontSize: 11, color: program.color, background: 'none', border: `1px solid ${program.color}44`, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', flexShrink: 0 }}
                    >
                      ✓ {nl ? 'Klaar' : 'Done'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full schedule toggle */}
      {isStarted && (
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ background: 'none', border: `1px solid rgba(255,255,255,0.1)`, color: C.silver2, fontSize: 12, padding: '7px 14px', borderRadius: 10, cursor: 'pointer', width: '100%', marginBottom: expanded ? 10 : 0 }}
        >
          {expanded ? '▲' : '▼'} {nl ? 'Volledig schema' : 'Full schedule'}
        </button>
      )}

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: program.weeks }, (_, wi) => {
            const base = wi * program.sessionsPerWeek;
            return (
              <div key={wi} style={{ padding: '8px 12px', background: wi + 1 === currentWeek ? `${program.color}0d` : 'transparent', border: `1px solid ${wi + 1 === currentWeek ? program.color + '33' : 'rgba(255,255,255,0.05)'}`, borderRadius: 10 }}>
                <div style={{ fontSize: 10, color: wi + 1 === currentWeek ? program.color : C.silver2, fontWeight: 700, marginBottom: 5, letterSpacing: 1 }}>
                  {nl ? `WEEK ${wi + 1}` : `WEEK ${wi + 1}`} {wi + 1 === currentWeek ? (nl ? '← NU' : '← NOW') : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[0, 1, 2].map(si => {
                    const sess = program.schedule[(base + si) % program.schedule.length];
                    return sess.exIds.map(exId => {
                      const meta = EX_META[exId] || { icon: '⚡', label: exId };
                      return (
                        <span key={exId} style={{ fontSize: 11, color: C.silver2, background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: '3px 8px' }}>
                          {meta.icon} {nl ? (meta.labelNL || meta.label) : meta.label}
                        </span>
                      );
                    });
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CTA */}
      {!isStarted ? (
        <button
          onClick={handleStart}
          style={{
            width: '100%', padding: '13px 0', borderRadius: 14, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg,${program.color},${program.color}bb)`,
            color: '#0a1220', fontWeight: 700, fontSize: 14, marginTop: 4,
          }}
        >
          🚀 {nl ? 'Start Programma' : 'Start Program'}
        </button>
      ) : (
        <button
          onClick={() => onStartExercise && onStartExercise(weekSessions[0].exIds[0])}
          style={{
            width: '100%', padding: '12px 0', borderRadius: 14, border: `1px solid ${program.color}66`, cursor: 'pointer',
            background: `${program.color}15`, color: program.color, fontWeight: 700, fontSize: 13, marginTop: 4,
          }}
        >
          ▶ {nl ? "Start de oefening van vandaag" : "Start today's exercise"}
        </button>
      )}
    </div>
  );
}

/* ─── Locked overlay ─────────────────────────────────────────────── */
function LockedOverlay({ nl }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '0 28px',
      background: 'rgba(8,10,20,0.78)', backdropFilter: 'blur(3px)',
      borderRadius: 22, zIndex: 10,
    }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>🔒</div>
      <div style={{ color: C.white, fontFamily: 'Georgia,serif', fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 10 }}>
        {nl ? 'Speciale Trainingsprogramma\'s' : 'Special Training Programs'}
      </div>
      <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.7, textAlign: 'center', maxWidth: 300 }}>
        {nl ? 'Upgrade naar een betaald account voor toegang tot gestructureerde meerdere-weken programma\'s.' : 'Upgrade to a paid account to access structured multi-week training programs.'}
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export default function ProgramsTab({ lang, license, exHistory, onStartExercise }) {
  const nl = lang === 'nl';
  const isPremium = license && license !== 'freemium';
  // Force re-render when localStorage changes (markDone)
  const [tick, setTick] = useState(0);
  React.useEffect(() => {
    const handler = () => setTick(t => t + 1);
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <div className="fadeUp">
      <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, marginBottom: 4 }}>
        🏆 {nl ? 'Speciale Trainingsprogramma\'s' : 'Special Training Programs'}
      </div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 18, fontWeight: 300 }}>
        {nl ? 'Gestructureerde meerdere-weken trainingsreizen.' : 'Structured multi-week training journeys.'}
      </div>

      {isPremium ? (
        <div>
          {PROGRAMS.map(p => (
            <ProgramCard key={p.id} program={p} nl={nl} isPremium={isPremium} onStartExercise={onStartExercise} exHistory={exHistory} />
          ))}
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {PROGRAMS.map(p => (
            <div key={p.id} style={{ ...s_GC({ padding: '18px 20px', marginBottom: 14 }), filter: 'blur(3px)', opacity: 0.4, pointerEvents: 'none', userSelect: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: 16, background: `${p.color}18`, border: `1px solid ${p.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>{p.icon}</div>
                <div>
                  <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: 'Georgia,serif' }}>{nl ? p.titleNL : p.title}</div>
                  <div style={{ color: C.silver2, fontSize: 12 }}>{nl ? p.subtitleNL : p.subtitle}</div>
                </div>
              </div>
            </div>
          ))}
          <LockedOverlay nl={nl} />
        </div>
      )}
    </div>
  );
}
